<template>
    <!-- Блок формы для изменения/создания товара -->
    <div class="product-modify-form">
        <!-- Заголовок формы -->
        <div class="modify-form-header">
            <div class="header-icon">{{ isAdd ? '➕' : '✏️' }}</div>
            <h1>{{ isAdd ? 'Добавить товар' : 'Редактировать товар' }}</h1>
            <p class="header-subtitle">{{ isAdd ? 'Заполните информацию о новом товаре' : 'Измените данные товара' }}</p>
        </div>

        <!-- Основная форма -->
        <form @submit.prevent="submitForm" class="product-form">
            <!-- Основная информация -->
            <div class="form-section">
                <h2 class="section-title">
                    <span class="section-icon">📋</span>
                    Основная информация
                </h2>

                <div class="form-grid">
                    <!-- Название товара -->
                    <div class="form-group">
                        <label for="productName" class="form-label">
                            <span class="label-icon">🏷️</span>
                            Название товара *
                        </label>
                        <input
                            type="text"
                            id="productName"
                            v-model="formData.name"
                            placeholder="Введите название товара"
                            required
                            class="form-input"
                        />
                    </div>

                    <!-- Категория -->
                    <div class="form-group">
                        <label for="category" class="form-label">
                            <span class="label-icon">📁</span>
                            Категория
                        </label>
                        <select
                            id="category"
                            v-model="formData.category_id"
                            class="form-select"
                            required
                        >
                            <option selected disabled>Выберите категорию</option>
                            <option
                                v-for="category in categories"
                                :key="category.id"
                                :value="category.id"
                            >
                                {{ category.name }}
                            </option>
                        </select>
                    </div>

                    <!-- НОВОЕ: Флаг товара из-за рубежа -->
                    <div class="form-group">
                        <label class="form-label">
                            <span class="label-icon">🌍</span>
                            Тип товара
                        </label>
                        <div class="checkbox-group">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="formData.is_pre_order_product"
                                    class="checkbox-input"
                                />
                                <span class="checkbox-custom"></span>
                                <span class="checkbox-text">Товар из-за рубежа (пре-ордер)</span>
                            </label>
                            <p class="checkbox-hint">
                                Отметьте, если товар заказывается из-за границы и требует времени на доставку
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Партии товара -->
            <div class="form-section">
                <div class="section-header-compact">
                    <h2 class="section-title">
                        <span class="section-icon">📦</span>
                        {{ isAdd ? 'Первая партия' : 'Партии' }}
                    </h2>
                    <button
                        v-if="!isAdd"
                        type="button"
                        @click="createBatch"
                        class="add-size-btn-compact"
                        :disabled="isCreatingBatch"
                    >
                        <span class="add-icon">+</span>
                        Добавить партию
                    </button>
                </div>

                <p class="checkbox-hint" v-if="isAdd">
                    Это будет ведущая партия товара. Остальные партии можно добавить после создания товара.
                </p>

                <div
                    v-for="batch in batches"
                    :key="batch._uid"
                    class="batch-card"
                    :class="{ leading: batch.is_leading }"
                >
                    <div class="batch-card-header">
                        <input
                            type="text"
                            v-model="batch.label"
                            placeholder="Название партии (например «Партия от 05.10»)"
                            class="form-input batch-label-input"
                        />
                        <span class="batch-leading-badge" v-if="batch.is_leading">Ведущая</span>
                    </div>

                    <div class="form-grid">
                        <div class="form-group">
                            <label class="form-label">📥 Закупочная цена *</label>
                            <div class="price-input-group">
                                <input type="number" v-model.number="batch.purchase_price" step="0.01" min="0" required class="form-input" />
                                <span class="currency">₽</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <label class="form-label">🚚 Стоимость доставки *</label>
                            <div class="price-input-group">
                                <input type="number" v-model.number="batch.delivery_price" step="0.01" min="0" required class="form-input" />
                                <span class="currency">₽</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <label class="form-label">💱 Курс обмена</label>
                            <input type="number" v-model.number="batch.exchange_rate" step="0.0001" min="0" class="form-input" />
                        </div>
                        <div class="form-group">
                            <label class="form-label">🛍 Цена дроп</label>
                            <div class="price-input-group">
                                <input type="number" v-model.number="batch.drop_price" step="0.01" min="0" class="form-input" />
                                <span class="currency">₽</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <label class="form-label">🏷 Цена розница</label>
                            <div class="price-input-group">
                                <input type="number" v-model.number="batch.retail_price" step="0.01" min="0" class="form-input" />
                                <span class="currency">₽</span>
                            </div>
                        </div>
                    </div>

                    <div class="section-header-compact batch-sizes-header">
                        <h3 class="section-title batch-sizes-title">Наличие по размерам</h3>
                        <button
                            type="button"
                            @click="addNewSizeToBatch(batch)"
                            class="add-size-btn-compact"
                            :disabled="!hasAvailableSizes || !filteredSizesForBatch(batch).length"
                        >
                            <span class="add-icon">+</span>
                            Размер
                        </button>
                    </div>

                    <div v-if="!batch.sizes.length" class="no-sizes-message">
                        <span class="warning-icon">⚠️</span>
                        <span>Нет ни одного размера в этой партии</span>
                    </div>

                    <div v-else class="sizes-compact-grid">
                        <div v-for="sizePresence in [...batch.sizes].reverse()" :key="sizePresence._uid" class="size-compact-card">
                            <div class="size-compact-header">
                                <div class="size-select-wrapper">
                                    <select v-model="sizePresence.size_id" class="size-select-compact" required>
                                        <option value="">Выберите размер</option>
                                        <option
                                            v-for="size in getAvailableSizesForBatchItem(batch, sizePresence)"
                                            :key="size.id"
                                            :value="size.id"
                                        >
                                            {{ size.name }}
                                        </option>
                                    </select>
                                </div>
                                <button
                                    type="button"
                                    @click="removeSizeFromBatch(batch, sizePresence)"
                                    class="remove-size-btn-compact"
                                >
                                    ✕
                                </button>
                            </div>
                            <div class="size-quantity-wrapper">
                                <input type="number" v-model.number="sizePresence.quantity" min="0" placeholder="Кол-во" required class="quantity-input-compact" />
                                <span class="quantity-label">шт.</span>
                            </div>
                        </div>
                    </div>

                    <BatchSizeTableEditor
                        :initial-table="batch.size_table"
                        @change="batch.size_table = $event"
                        @error="showError"
                    />

                    <div class="batch-card-actions" v-if="!isAdd">
                        <button
                            type="button"
                            class="btn btn-cancel batch-action-btn"
                            v-if="!batch.is_leading && batches.length > 1"
                            @click="deleteBatchRow(batch)"
                            :disabled="batch._saving"
                        >
                            Удалить партию
                        </button>
                        <button
                            type="button"
                            class="btn batch-action-btn make-leading-btn"
                            v-if="!batch.is_leading"
                            @click="makeLeadingBatch(batch)"
                            :disabled="batch._saving"
                        >
                            Сделать ведущей
                        </button>
                        <button
                            type="button"
                            class="btn btn-submit batch-action-btn"
                            @click="saveBatch(batch)"
                            :disabled="batch._saving"
                        >
                            {{ batch._saving ? 'Сохранение...' : 'Сохранить партию' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Фото товара -->
            <div class="form-section">
                <h2 class="section-title">
                    <span class="section-icon">📷</span>
                    Фотография товара
                </h2>

                <div class="photo-upload-section">
                    <div class="file-upload-compact">
                        <input
                            type="file"
                            id="photoUpload"
                            accept="image/*"
                            @change="handlePhotoUpload"
                            class="file-input"
                        />
                        <label for="photoUpload" class="file-upload-label-compact">
                            <span class="upload-icon-compact">📁</span>
                            <span class="upload-text-compact">
                                {{ displayedPhoto ? 'Изменить фото' : 'Загрузить фото' }}
                            </span>
                        </label>
                    </div>

                    <div v-if="displayedPhoto" class="photo-preview-compact">
                        <img :src="displayedPhoto" alt="Preview" class="preview-img-compact" />
                        <button
                            type="button"
                            @click="removePhoto"
                            class="remove-photo-btn-compact"
                        >
                            ✕
                        </button>
                    </div>
                </div>
            </div>

            <!-- Кнопки действий -->
            <div class="form-actions">
                <button
                    type="button"
                    @click="$emit('cancel')"
                    class="btn btn-cancel"
                >
                    Отмена
                </button>
                <button
                    type="submit"
                    class="btn btn-submit"
                    :disabled="isSubmitting"
                >
                    <span v-if="isSubmitting" class="submit-spinner"></span>
                    <span v-else>{{ isAdd ? 'Создать товар' : 'Сохранить изменения' }}</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import api from '@/api/api';
import BatchSizeTableEditor from './BatchSizeTableEditor.vue';

export default {
    components: {
        BatchSizeTableEditor,
    },
    name: 'ProductModifyForm',
    props: {
        isAdd: {
            type: Boolean,
            default: true,
            required: true
        },
        initialData: {
            type: Object,
            default: () => ({})
        },
        categories: {
            type: Array,
            default: () => []
        },
        availableSizes: {
            type: Array,
            default: () => []
        },
        product: {
            type: Object,
            default: () => ({}),
            required: false
        }
    },
    emits: ['submit', 'cancel'],
    data() {
        return {
            formData: {
                name: '',
                category_id: null,
                is_pre_order_product: false,
                selectedFile: null,
            },

            // Партии товара. В режиме добавления — всегда ровно одна (будущая ведущая).
            // В режиме редактирования — реальные партии товара, каждая сохраняется отдельно.
            batches: [],

            // Превью изображения
            photo: null,

            // Кэшируем отсортированные размеры
            sortedSizes: [],
            // Уникальный идентификатор для каждого элемента размера/партии
            sizeCounter: 0,
            batchCounter: 0,
            // Флаг загрузки размеров
            isLoadingSizes: true,
            // Флаг отправки формы
            isSubmitting: false,
            isCreatingBatch: false,
            // Флаг, что форма заполнена данными товара
            isFormFilled: false,
        }
    },
    computed: {
        // Показываем фото из пропсов или загруженное пользователем
        displayedPhoto() {
            return this.photo || (this.product && this.product.photo) || null;
        },

        hasAvailableSizes() {
            return this.sortedSizes && this.sortedSizes.length > 0;
        },
    },
    created() {
        this.sortSizes();

        if (this.isAdd) {
            if (this.initialData && Object.keys(this.initialData).length > 0) {
                this.formData = { ...this.formData, ...this.initialData };
            }
            this.batches = [this.makeEmptyBatch(true)];
        } else {
            if (this.product && Object.keys(this.product).length > 0) {
                this.fillFormWithProductData(this.product);
            }
        }
    },
    mounted() {
        if (!this.hasAvailableSizes && this.availableSizes.length === 0) {
            this.checkSizesLoaded();
        }
    },
    watch: {
        availableSizes: {
            handler(newSizes) {
                if (newSizes && newSizes.length > 0) {
                    this.sortSizes();
                }
            },
            deep: true,
            immediate: true
        },

        product: {
            handler(newProduct) {
                if (newProduct && Object.keys(newProduct).length > 0 && !this.isAdd && !this.isFormFilled) {
                    this.fillFormWithProductData(newProduct);
                    this.isFormFilled = true;
                }
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        // ---- Категории/размеры (справочники) ----

        findCategoryIdByName(categoryName) {
            if (!categoryName) return null;
            const category = this.categories.find(c => c.name === categoryName);
            return category ? category.id : null;
        },

        checkSizesLoaded() {
            if (this.availableSizes && this.availableSizes.length > 0) {
                this.sortSizes();
                this.isLoadingSizes = false;
            } else {
                setTimeout(() => this.checkSizesLoaded(), 100);
            }
        },

        sortSizes() {
            if (!this.availableSizes || this.availableSizes.length === 0) {
                this.sortedSizes = [];
                this.isLoadingSizes = false;
                return;
            }

            const sizeOrder = ['clothing', 'shoe_single', 'shoe_double'];
            const getCategory = (sizeName) => {
                if (!sizeName) return 'clothing';
                const nameLower = sizeName.toLowerCase();
                const clothingSizes = ['s', 'm', 'l', 'xl', '2xl', '3xl', '4xl', '5xl', 'ones'];
                if (clothingSizes.includes(nameLower)) return 'clothing';
                if (sizeName.includes('-') || sizeName.includes('/')) return 'shoe_double';
                if (/^\d+$/.test(sizeName)) return 'shoe_single';
                return 'clothing';
            };

            this.sortedSizes = [...this.availableSizes].sort((a, b) => {
                const categoryA = getCategory(a.name);
                const categoryB = getCategory(b.name);
                if (categoryA !== categoryB) {
                    return sizeOrder.indexOf(categoryA) - sizeOrder.indexOf(categoryB);
                }
                if (categoryA === 'clothing') {
                    const clothingOrder = { 'S': 1, 'M': 2, 'L': 3, 'XL': 4, '2XL': 5, '3XL': 6, '4XL': 7, '5XL': 8, 'Ones': 9 };
                    return (clothingOrder[a.name] || 99) - (clothingOrder[b.name] || 99);
                } else if (categoryA === 'shoe_single') {
                    return (parseInt(a.name) || 0) - (parseInt(b.name) || 0);
                } else if (categoryA === 'shoe_double') {
                    const firstNumA = parseInt(a.name.split('-')[0].split('/')[0]) || 0;
                    const firstNumB = parseInt(b.name.split('-')[0].split('/')[0]) || 0;
                    return firstNumA - firstNumB;
                }
                return 0;
            });

            this.isLoadingSizes = false;
        },

        findSizeNameById(sizeId) {
            const size = this.availableSizes.find(s => s.id === sizeId);
            return size ? size.name : null;
        },

        // ---- Партии ----

        makeEmptyBatch(isLeading) {
            this.batchCounter += 1;
            return {
                _uid: `batch_${this.batchCounter}`,
                id: null,
                label: isLeading ? 'Партия 1' : '',
                purchase_price: 0,
                delivery_price: 0,
                exchange_rate: 1.0,
                drop_price: null,
                retail_price: null,
                size_table: [],
                is_leading: isLeading,
                sizes: [],
                _saving: false,
            };
        },

        makeSizeRow(sizeId, quantity) {
            this.sizeCounter += 1;
            return { _uid: `size_${this.sizeCounter}`, size_id: sizeId, quantity };
        },

        fillFormWithProductData(product) {
            const categoryId = product.category
                ? this.findCategoryIdByName(product.category)
                : (product.category_id || null);

            this.formData = {
                name: product.name || '',
                category_id: categoryId,
                is_pre_order_product: product.is_pre_order_product || false,
                selectedFile: null,
            };

            const sourceBatches = Array.isArray(product.batches) ? product.batches : [];
            if (sourceBatches.length) {
                this.batches = sourceBatches.map(b => ({
                    _uid: `batch_${this.batchCounter++}`,
                    id: b.id,
                    label: b.label || '',
                    purchase_price: b.purchase_price,
                    delivery_price: b.delivery_price,
                    exchange_rate: b.exchange_rate,
                    drop_price: b.drop_price,
                    retail_price: b.retail_price,
                    size_table: b.size_table,
                    is_leading: b.is_leading,
                    sizes: (b.presence || []).map(p => this.makeSizeRow(p.size_id, p.quantity)),
                    _saving: false,
                }));
            } else {
                // Товар создан до введения партий и ещё не мигрирован — не должно
                // происходить в проде (бэкфилл создаёт партию каждому товару),
                // но на всякий случай не оставляем форму пустой.
                this.batches = [this.makeEmptyBatch(true)];
            }

            if (product.photo && product.photo !== 'string') {
                this.photo = product.photo;
            }

            this.isFormFilled = true;
        },

        filteredSizesForBatch(batch) {
            const selectedIds = batch.sizes.map(s => s.size_id).filter(id => id !== null && id !== undefined && id !== '');
            return this.sortedSizes.filter(size => !selectedIds.includes(size.id));
        },

        getAvailableSizesForBatchItem(batch, sizePresence) {
            const currentId = sizePresence.size_id;
            if (currentId) {
                const current = this.sortedSizes.find(s => s.id === currentId);
                const others = this.filteredSizesForBatch(batch).filter(s => s.id !== currentId);
                return current ? [current, ...others] : others;
            }
            return this.filteredSizesForBatch(batch);
        },

        addNewSizeToBatch(batch) {
            if (!this.hasAvailableSizes) {
                this.showError('Размеры ещё не загружены. Подождите немного.');
                return;
            }
            if (this.filteredSizesForBatch(batch).length === 0) {
                this.showError('Все доступные размеры уже добавлены в эту партию');
                return;
            }
            batch.sizes.push(this.makeSizeRow(null, 0));
        },

        removeSizeFromBatch(batch, sizePresence) {
            const index = batch.sizes.findIndex(s => s._uid === sizePresence._uid);
            if (index !== -1) {
                batch.sizes.splice(index, 1);
            }
        },

        buildBatchPayload(batch) {
            return {
                label: batch.label || null,
                purchase_price: parseFloat(batch.purchase_price),
                delivery_price: parseFloat(batch.delivery_price),
                exchange_rate: batch.exchange_rate !== null && batch.exchange_rate !== ''
                    ? parseFloat(batch.exchange_rate) : null,
                drop_price: batch.drop_price !== null && batch.drop_price !== '' ? parseFloat(batch.drop_price) : null,
                retail_price: batch.retail_price !== null && batch.retail_price !== '' ? parseFloat(batch.retail_price) : null,
                size_table: batch.size_table,
                sizes: batch.sizes
                    .filter(s => s.size_id && s.quantity >= 0)
                    .map(s => ({ size_id: parseInt(s.size_id), quantity: parseInt(s.quantity) })),
            };
        },

        async createBatch() {
            if (this.isCreatingBatch) return;
            this.isCreatingBatch = true;
            try {
                const response = await api.post(
                    `/accounting_system/products/${this.product.id}/batches`,
                    { ...this.buildBatchPayload(this.makeEmptyBatch(false)), is_leading: false }
                );
                const b = response.data;
                this.batches.push({
                    _uid: `batch_${this.batchCounter++}`,
                    id: b.id,
                    label: b.label || '',
                    purchase_price: b.purchase_price,
                    delivery_price: b.delivery_price,
                    exchange_rate: b.exchange_rate,
                    drop_price: b.drop_price,
                    retail_price: b.retail_price,
                    size_table: b.size_table,
                    is_leading: b.is_leading,
                    sizes: (b.presence || []).map(p => this.makeSizeRow(p.size_id, p.quantity)),
                    _saving: false,
                });
            } catch (error) {
                this.showError(this.extractErrorMessage(error, 'Не удалось создать партию'));
            } finally {
                this.isCreatingBatch = false;
            }
        },

        async saveBatch(batch) {
            if (!batch.purchase_price && batch.purchase_price !== 0) {
                this.showError('Укажите закупочную цену партии');
                return;
            }
            batch._saving = true;
            try {
                const response = await api.put(`/accounting_system/batches/${batch.id}`, this.buildBatchPayload(batch));
                const b = response.data;
                batch.label = b.label || '';
                batch.purchase_price = b.purchase_price;
                batch.delivery_price = b.delivery_price;
                batch.exchange_rate = b.exchange_rate;
                batch.drop_price = b.drop_price;
                batch.retail_price = b.retail_price;
                batch.sizes = (b.presence || []).map(p => this.makeSizeRow(p.size_id, p.quantity));
                this.showSuccess('Партия сохранена');
            } catch (error) {
                this.showError(this.extractErrorMessage(error, 'Не удалось сохранить партию'));
            } finally {
                batch._saving = false;
            }
        },

        async makeLeadingBatch(batch) {
            batch._saving = true;
            try {
                await api.post(`/accounting_system/batches/${batch.id}/make-leading`);
                this.batches.forEach(b => { b.is_leading = (b._uid === batch._uid); });
            } catch (error) {
                this.showError(this.extractErrorMessage(error, 'Не удалось сделать партию ведущей'));
            } finally {
                batch._saving = false;
            }
        },

        async deleteBatchRow(batch) {
            if (!confirm('Удалить эту партию вместе с её остатками?')) return;
            batch._saving = true;
            try {
                await api.delete(`/accounting_system/batches/${batch.id}`);
                this.batches = this.batches.filter(b => b._uid !== batch._uid);
            } catch (error) {
                this.showError(this.extractErrorMessage(error, 'Не удалось удалить партию'));
                batch._saving = false;
            }
        },

        // ---- Отправка формы ----

        async submitForm() {
            try {
                if (this.isSubmitting) return;

                if (!this.formData.name || !this.formData.name.trim()) {
                    this.showError('Введите название товара');
                    return;
                }
                if (!this.formData.category_id) {
                    this.showError('Выберите категорию');
                    return;
                }

                this.isSubmitting = true;

                if (this.isAdd) {
                    await this.submitNewProduct();
                } else {
                    await this.submitBaseUpdate();
                }
            } finally {
                this.isSubmitting = false;
            }
        },

        async submitNewProduct() {
            const batch = this.batches[0];
            if (!batch.purchase_price && batch.purchase_price !== 0) {
                this.showError('Закупочная цена должна быть больше 0');
                return;
            }
            const validSizes = batch.sizes.filter(s => s.size_id && s.quantity >= 0);
            if (validSizes.length === 0) {
                this.showError('Добавьте хотя бы один размер с количеством в первую партию');
                return;
            }

            const productData = {
                name: this.formData.name.trim(),
                category_id: parseInt(this.formData.category_id),
                purchase_price: parseFloat(batch.purchase_price),
                delivery_price: parseFloat(batch.delivery_price),
                exchange_rate: parseFloat(batch.exchange_rate || 1.0),
                drop_price: batch.drop_price !== null && batch.drop_price !== '' ? parseFloat(batch.drop_price) : null,
                retail_price: batch.retail_price !== null && batch.retail_price !== '' ? parseFloat(batch.retail_price) : null,
                size_table: batch.size_table,
                is_pre_order_product: Boolean(this.formData.is_pre_order_product),
                sizes: validSizes.map(s => ({ size_id: parseInt(s.size_id), quantity: parseInt(s.quantity) })),
            };

            try {
                const formData = new FormData();
                formData.append('product_data', JSON.stringify(productData));
                if (this.formData.selectedFile) {
                    formData.append('photo', this.formData.selectedFile);
                }

                const response = await api.post('/accounting_system/products', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                const productFromServer = response.data.product || response.data;
                this.showSuccess('Товар успешно создан!');
                this.$emit('submit', productFromServer, 'created');
            } catch (error) {
                this.showError(this.extractErrorMessage(error, 'Ошибка при создании товара'));
            }
        },

        async submitBaseUpdate() {
            const productData = {
                name: this.formData.name.trim(),
                category_id: parseInt(this.formData.category_id),
                is_pre_order_product: Boolean(this.formData.is_pre_order_product),
            };

            try {
                const formData = new FormData();
                formData.append('product_data', JSON.stringify(productData));
                if (this.formData.selectedFile) {
                    formData.append('photo', this.formData.selectedFile);
                }

                if (!this.product || !this.product.id) {
                    throw new Error('Не указан ID товара для обновления');
                }

                const response = await api.put(`/accounting_system/products/${this.product.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                const productFromServer = response.data.product || response.data;
                this.showSuccess('Товар успешно обновлён!');
                this.$emit('submit', productFromServer, 'updated');
            } catch (error) {
                this.showError(this.extractErrorMessage(error, 'Ошибка при обновлении товара'));
            }
        },

        extractErrorMessage(error, fallback) {
            const data = error.response?.data;
            if (data?.detail) {
                if (Array.isArray(data.detail)) {
                    return data.detail.map(err => `${err.loc?.join('.') || 'unknown'}: ${err.msg}`).join('\n');
                }
                if (typeof data.detail === 'object') return data.detail.msg || fallback;
                return data.detail;
            }
            return data?.message || error.message || fallback;
        },

        // ---- Фото ----

        removePhoto() {
            this.photo = null;
            this.formData.selectedFile = null;
        },

        handlePhotoUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (file.size > 5 * 1024 * 1024) {
                this.showError('Файл слишком большой. Максимальный размер: 5MB');
                event.target.value = '';
                return;
            }
            if (!file.type.startsWith('image/')) {
                this.showError('Пожалуйста, выберите изображение');
                event.target.value = '';
                return;
            }

            this.formData.selectedFile = file;
            const reader = new FileReader();
            reader.onload = (e) => { this.photo = e.target.result; };
            reader.onerror = () => {
                this.showError('Ошибка чтения файла');
                this.formData.selectedFile = null;
                this.photo = null;
            };
            reader.readAsDataURL(file);
            event.target.value = '';
        },

        showSuccess(message) {
            alert(`✅ ${message}`);
        },

        showError(message) {
            alert(`❌ ${message}`);
        },
    }
}
</script>

<style scoped>
.product-modify-form {
    width: 100%;
    max-height: 100vh;
    padding: 30vw 20px 20px 20px;
    background: linear-gradient(135deg, #181818 0%, #1a1a2e 100%);
    color: #ffffff;
    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    overflow-y: auto;
    z-index: 1000;
    box-sizing: order-box;
}

/* Заголовок */
.modify-form-header {
    text-align: center;
    margin-bottom: 30px;
    padding-bottom: 15px;
    border-bottom: 2px solid rgba(37, 165, 240, 0.3);
}

.header-icon {
    font-size: 40px;
    margin-bottom: 12px;
    animation: float 3s ease-in-out infinite;
}

.modify-form-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 6px 0;
    background: linear-gradient(135deg, #25A5F0 0%, #4ECDC4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.header-subtitle {
    color: #a0a0a0;
    font-size: 14px;
    margin: 0;
}

/* Секции формы */
.form-section {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
}

.form-section:hover {
    border-color: rgba(37, 165, 240, 0.3);
    box-shadow: 0 4px 20px rgba(37, 165, 240, 0.1);
}

.section-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 10px;
    color: #25A5F0;
}

.section-icon {
    font-size: 20px;
}

/* Сетка формы */
.form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
}

/* Группы форм */
.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-label {
    font-size: 13px;
    font-weight: 600;
    color: #e0e0e0;
    display: flex;
    align-items: center;
    gap: 6px;
}

.label-icon {
    font-size: 14px;
}

/* Чекбокс */
.checkbox-group {
    margin-top: 4px;
}

.checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: background-color 0.3s ease;
}

.checkbox-label:hover {
    background: rgba(37, 165, 240, 0.1);
}

.checkbox-input {
    display: none;
}

.checkbox-custom {
    width: 20px;
    height: 20px;
    min-width: 20px;
    border: 2px solid rgba(37, 165, 240, 0.5);
    border-radius: 4px;
    position: relative;
    transition: all 0.3s ease;
    margin-top: 2px;
}

.checkbox-input:checked + .checkbox-custom {
    background-color: #25A5F0;
    border-color: #25A5F0;
}

.checkbox-input:checked + .checkbox-custom::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 12px;
    font-weight: bold;
}

.checkbox-text {
    font-size: 14px;
    color: #ffffff;
    line-height: 1.4;
}

.checkbox-hint {
    margin: 8px 0 0 30px;
    font-size: 12px;
    color: #a0a0a0;
    line-height: 1.4;
}

/* Поля ввода */
.form-input,
.form-select {
    width: 100%;
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: #ffffff;
    font-size: 14px;
    transition: all 0.3s ease;
}

.form-input:focus,
.form-select:focus {
    outline: none;
    border-color: #25A5F0;
    box-shadow: 0 0 0 2px rgba(37, 165, 240, 0.2);
    background: rgba(255, 255, 255, 0.12);
}

.form-input::placeholder {
    color: rgba(255, 255, 255, 0.4);
}

/* Группа цены */
.price-input-group {
    position: relative;
}

.price-input-group .form-input {
    padding-right: 40px;
}

.currency {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #25A5F0;
    font-weight: 600;
    font-size: 14px;
}

/* Партии */
.batch-card {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
}

.batch-card.leading {
    border-color: rgba(46, 204, 113, 0.5);
}

.batch-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
}

.batch-label-input {
    flex: 1;
}

.batch-leading-badge {
    background-color: #2ecc71;
    color: #06240f;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
}

.batch-sizes-header {
    margin-top: 18px;
}

.batch-sizes-title {
    font-size: 14px;
    color: #e0e0e0;
}

.batch-card-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.batch-action-btn {
    min-width: auto;
    padding: 8px 16px;
    font-size: 13px;
}

.make-leading-btn {
    background: rgba(46, 204, 113, 0.15);
    color: #2ecc71;
    border: 1px solid rgba(46, 204, 113, 0.4);
}

/* Компактные размеры */
.section-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.add-size-btn-compact {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: rgba(37, 165, 240, 0.1);
    border: 1px solid rgba(37, 165, 240, 0.3);
    border-radius: 8px;
    color: #25A5F0;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.add-size-btn-compact:hover {
    background: rgba(37, 165, 240, 0.2);
    border-color: #25A5F0;
}

.add-size-btn-compact:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
}

.add-icon {
    font-size: 18px;
    font-weight: bold;
}

.sizes-compact-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
}

.size-compact-card {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    padding: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
}

.size-compact-card:hover {
    border-color: rgba(37, 165, 240, 0.3);
}

.size-compact-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
}

.size-select-wrapper {
    flex: 1;
}

.size-select-compact {
    width: 100%;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    color: #ffffff;
    font-size: 13px;
    transition: all 0.3s ease;
}

.size-select-compact:focus {
    outline: none;
    border-color: #25A5F0;
    box-shadow: 0 0 0 2px rgba(37, 165, 240, 0.2);
}

.remove-size-btn-compact {
    background: rgba(255, 59, 48, 0.2);
    border: none;
    color: #ff3b30;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 11px;
    flex-shrink: 0;
}

.remove-size-btn-compact:hover {
    background: rgba(255, 59, 48, 0.3);
}

.size-quantity-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.quantity-input-compact {
    flex: 1;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    color: #ffffff;
    font-size: 13px;
    transition: all 0.3s ease;
}

.quantity-input-compact:focus {
    outline: none;
    border-color: #25A5F0;
}

.quantity-label {
    color: #a0a0a0;
    font-size: 12px;
    font-weight: 500;
    min-width: 30px;
}

/* Загрузка фото */
.photo-upload-section {
    display: flex;
    align-items: center;
    gap: 16px;
}

.file-upload-compact {
    flex: 1;
}

.file-input {
    display: none;
}

.file-upload-label-compact {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: rgba(37, 165, 240, 0.1);
    border: 2px dashed rgba(37, 165, 240, 0.3);
    border-radius: 8px;
    color: #25A5F0;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: center;
    justify-content: center;
}

.file-upload-label-compact:hover {
    background: rgba(37, 165, 240, 0.2);
    border-color: #25A5F0;
}

.upload-icon-compact {
    font-size: 16px;
}

.upload-text-compact {
    white-space: nowrap;
}

.photo-preview-compact {
    position: relative;
    width: 80px;
    height: 80px;
    flex-shrink: 0;
}

.preview-img-compact {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid rgba(255, 255, 255, 0.1);
}

.remove-photo-btn-compact {
    position: absolute;
    top: -6px;
    right: -6px;
    background: rgba(0, 0, 0, 0.8);
    border: none;
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 10px;
    transition: all 0.3s ease;
}

.remove-photo-btn-compact:hover {
    background: rgba(255, 59, 48, 0.8);
    transform: scale(1.1);
}

/* Кнопки действий */
.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.btn {
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
}

.btn-cancel {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
}

.btn-cancel:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
}

.btn-submit {
    background: linear-gradient(135deg, #25A5F0 0%, #4ECDC4 100%);
    color: white;
}

.btn-submit:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(37, 165, 240, 0.3);
}

.submit-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Анимации */
@keyframes float {
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-8px);
    }
}

/* Адаптивность */
@media (max-width: 768px) {
    .product-modify-form {
        padding: 30vw 16px 16px 16px;
    }

    .form-grid {
        grid-template-columns: 1fr;
    }

    .sizes-compact-grid {
        grid-template-columns: 1fr;
    }

    .photo-upload-section {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    .photo-preview-compact {
        width: 100px;
        height: 100px;
        align-self: center;
    }

    .form-actions {
        flex-direction: column;
    }

    .btn {
        min-width: 100%;
    }

    .section-header-compact {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .add-size-btn-compact {
        align-self: stretch;
        justify-content: center;
    }

    .checkbox-hint {
        margin-left: 26px;
    }
}

@media (max-width: 480px) {
    .modify-form-header h1 {
        font-size: 20px;
    }

    .section-title {
        font-size: 16px;
    }

    .btn {
        padding: 10px 20px;
        font-size: 13px;
    }
}

/* Скроллбар */
.product-modify-form::-webkit-scrollbar {
    width: 5px;
}

.product-modify-form::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
}

.product-modify-form::-webkit-scrollbar-thumb {
    background: #25A5F0;
    border-radius: 3px;
}

.product-modify-form::-webkit-scrollbar-thumb:hover {
    background: #1e8bc4;
}

/* Стили для загрузки размеров */
.loading-sizes {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 8px;
    gap: 12px;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(37, 165, 240, 0.2);
    border-top-color: #25A5F0;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-sizes span {
    color: #a0a0a0;
    font-size: 14px;
}

/* Сообщение о отсутствии размеров */
.no-sizes-message {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(255, 165, 0, 0.1);
    border: 1px solid rgba(255, 165, 0, 0.3);
    border-radius: 8px;
    gap: 10px;
}

.warning-icon {
    font-size: 20px;
}

.no-sizes-message span:last-child {
    color: #ffa500;
    font-size: 14px;
    text-align: center;
}
</style>
